import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import { getIsDSTFlow } from '@cont/DST/utils';
import useFetch from '@hooks/useFetch';
import useTopology from '@api/hooks/topology/useTopology';
import usePlusIconicSubscribed from '@hooks/usePlusIconicSubscribed';
import { PrimeMode, UserPlatformPlan } from '@stores/prime-quota';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import usePrimeTrialPromoModal from '@stores/prime-pitch/usePrimeTrialPromoModal';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import { readCookie } from '@utils/cookies';

const useQuizPrimeQuota = (quizDetails) => {
  const { asPath } = useRouter();
  const goalUID = quizDetails?.goal_uid;
  const quizUID = quizDetails?.uid;
  const isSellable = Boolean(quizDetails?.is_sellable);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isPlusOrIconic = usePlusIconicSubscribed({ goalUID });
  const accessToken = readCookie('accessToken');

  const { topology: goalTopology } = useTopology(goalUID);
  const isPrimeGoal = getIsPrimeEnabled(goalTopology);
  const isScholarshipTest = getIsDSTFlow(asPath);

  const { data: uamData } = useFetch(
    isLoggedIn && isSellable && !isPlusOrIconic && quizUID
      ? `v1/user/check_access/?actions=Quiz:Join&resource=Quiz:${quizUID}`
      : null,
    { revalidateOnMount: true }
  );
  const hasStoreAccess = Boolean(uamData?.actionAllowed?.['quiz:join']);

  const fetchQuotaDetails = usePrimeQuotaStore(
    (state) => state.fetchQuotaDetails
  );
  const consumeQuota = usePrimeQuotaStore((state) => state.consumeQuota);
  const isFetchingQuotaDetails = usePrimeQuotaStore(
    (state) => state.isFetchingQuotaDetails
  );
  const userPlatformPlan = usePrimeQuotaStore(
    (state) => state.userPlatformPlan
  );
  const getQuotaForMode = usePrimeQuotaStore((state) => state.getQuotaForMode);
  const { openPrimeTrialPromoModal } = usePrimeTrialPromoModal();
  const shouldSkipPrimeQuota = useMemo(() => {
    if (!isPrimeGoal || !goalUID) {
      return true;
    }

    if (isPlusOrIconic) {
      return true;
    }

    if (isScholarshipTest) {
      return true;
    }

    if (isSellable && hasStoreAccess) {
      return true;
    }

    if (userPlatformPlan === UserPlatformPlan.PAID) {
      return true;
    }

    return false;
  }, [
    goalUID,
    hasStoreAccess,
    isPlusOrIconic,
    isPrimeGoal,
    isScholarshipTest,
    isSellable,
    userPlatformPlan
  ]);

  useEffect(() => {
    if (!isLoggedIn || !isPrimeGoal || !goalUID || !accessToken) {
      return;
    }

    if (isScholarshipTest || (isSellable && hasStoreAccess)) {
      return;
    }

    if (isPlusOrIconic) {
      return;
    }

    fetchQuotaDetails({ goalUID, accessToken });
  }, [
    accessToken,
    fetchQuotaDetails,
    goalUID,
    hasStoreAccess,
    isLoggedIn,
    isPlusOrIconic,
    isPrimeGoal,
    isScholarshipTest,
    isSellable
  ]);

  const primeTestQuota = getQuotaForMode(PrimeMode.TEST);

  const isPrimeQuotaExhausted =
    !shouldSkipPrimeQuota && primeTestQuota.isQuotaExhausted;

  const isPrimeQuotaLoading =
    isPrimeGoal &&
    isLoggedIn &&
    !shouldSkipPrimeQuota &&
    isFetchingQuotaDetails &&
    userPlatformPlan === null;

  const shouldApplyPrimeQuota =
    isPrimeGoal &&
    isLoggedIn &&
    !isPlusOrIconic &&
    !isScholarshipTest &&
    !(isSellable && hasStoreAccess);

  const blockIfPrimeQuotaExhausted = useCallback(() => {
    if (shouldSkipPrimeQuota || isFetchingQuotaDetails) {
      return false;
    }

    if (!isPrimeQuotaExhausted) {
      return false;
    }
    openPrimeTrialPromoModal(
      PrimeTrialPromoContextualId.TEST,
      goalUID,
      goalTopology?.slug,
      {
        dismissNavigateTo: `/goal/${goalTopology?.slug}/${goalUID}/test-series`
      }
    );

    return true;
  }, [
    goalTopology?.slug,
    goalUID,
    isFetchingQuotaDetails,
    isPrimeQuotaExhausted,
    openPrimeTrialPromoModal,
    shouldSkipPrimeQuota
  ]);

  const consumeTestQuotaBeforeStart = useCallback(async () => {
    if (shouldSkipPrimeQuota || !goalUID || !quizUID) {
      return;
    }

    await consumeQuota({
      goalUID,
      objectUID: quizUID,
      mode: PrimeMode.TEST,
      accessToken
    });
  }, [accessToken, consumeQuota, goalUID, quizUID, shouldSkipPrimeQuota]);

  return {
    shouldApplyPrimeQuota,
    isPrimeQuotaLoading,
    blockIfPrimeQuotaExhausted,
    consumeTestQuotaBeforeStart
  };
};

export default useQuizPrimeQuota;
